/**
 * SortableGrid — the store detail page's results grid.
 *
 * Mirrors the self-host dashboard's renderSortableGrid:
 *   - CLICK-TO-SORT headers. With `onSort`, sorting is delegated to the caller
 *     (server-side ORDER BY over the whole table) and `sortCol`/`sortDir` drive
 *     the ▲▼ indicator; without it, the CURRENTLY loaded rows sort client-side
 *     (numeric when the column parses as numbers, else string).
 *   - `rowStart` prepends a 1-based "#" row-number column starting there.
 *   - `colTypes` shows each column's declared SQLite type under its name and
 *     guards inline edits (a numeric column must not silently accept text —
 *     SQLite wouldn't error; it would just store text and break the column).
 *   - `onEdit(ri, ci, newValue, oldValue) → Promise` makes cells editable
 *     (double-click). The grid commits optimistically and reverts on rejection.
 *   - Drag a header's right edge to resize that column (fixed layout after the
 *     first auto-layout paint measures natural widths).
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

const cellText = (v) => (v == null ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v)));
const isNumericType = (t) => /INT|REAL|FLOA|DOUB|NUM|DEC/i.test(String(t || ''));
const NUM_COL_W = 52;

export default function SortableGrid({
  columns,
  rows,
  rowStart = null,
  colTypes = null,
  sortCol = -1,
  sortDir = 1,
  onSort,
  onEdit,
  onTypeError,
}) {
  const serverSort = typeof onSort === 'function';
  const editable = typeof onEdit === 'function';
  const showNum = rowStart != null;

  // Local row copy: client sort reorders it, inline edits patch it (so a later
  // client sort doesn't resurrect a pre-edit value). Reset when data changes.
  const [view, setView] = useState(rows || []);
  const [clientSort, setClientSort] = useState({ col: -1, dir: 1 });
  const [widths, setWidths] = useState(null);
  const [editing, setEditing] = useState(null); // { ri, ci, value }
  const tableRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    setView(rows || []);
    setClientSort({ col: -1, dir: 1 });
    setEditing(null);
  }, [rows]);
  // Remeasure widths only when the column SET changes (paging keeps them stable).
  useEffect(() => { setWidths(null); }, [JSON.stringify(columns)]); // eslint-disable-line react-hooks/exhaustive-deps

  const val = (r, i) => (Array.isArray(r) ? r[i] : r[columns[i]]);

  // First pass renders in auto layout → measure natural DATA-column widths,
  // grow the last column to fill any slack, then re-render FIXED so columns
  // become resizable + stable across page loads.
  useLayoutEffect(() => {
    if (widths || !view.length || !tableRef.current) return;
    const ths = tableRef.current.querySelectorAll('thead th.sortable');
    if (!ths.length) return;
    const w = Array.prototype.map.call(ths, (th) => Math.max(72, Math.min(520, th.offsetWidth)));
    const avail = wrapRef.current ? wrapRef.current.clientWidth : 0;
    const natural = (showNum ? NUM_COL_W : 0) + w.reduce((a, b) => a + b, 0);
    if (avail && natural < avail && w.length) w[w.length - 1] += (avail - natural - 2);
    setWidths(w);
  }, [widths, view, showNum]);

  if (!view.length) return <div className="sv-empty">No rows.</div>;

  const totalW = widths ? (showNum ? NUM_COL_W : 0) + widths.reduce((a, b) => a + b, 0) : null;
  const effSortCol = serverSort ? sortCol : clientSort.col;
  const effSortDir = serverSort ? sortDir : clientSort.dir;

  const handleSort = (i) => {
    if (serverSort) { onSort(i); return; }
    const dir = clientSort.col === i ? -clientSort.dir : 1;
    setClientSort({ col: i, dir });
    setView((prev) => [...prev].sort((a, b) => {
      const av = val(a, i);
      const bv = val(b, i);
      const an = parseFloat(av);
      const bn = parseFloat(bv);
      const numeric = av != null && bv != null && !Number.isNaN(an) && !Number.isNaN(bn)
        && String(av).trim() !== '' && String(bv).trim() !== '';
      if (numeric) return (an - bn) * dir;
      const as = av == null ? '' : String(av);
      const bs = bv == null ? '' : String(bv);
      return as < bs ? -dir : as > bs ? dir : 0;
    }));
  };

  // Drag a header's right edge to resize (live via the DOM, committed to state
  // on mouseup so React's next render keeps the width).
  const startResize = (e, i) => {
    e.preventDefault();
    e.stopPropagation();
    if (!widths) return;
    const startX = e.clientX;
    const startW = widths[i];
    const cols = tableRef.current ? tableRef.current.querySelectorAll('colgroup col') : [];
    const col = cols[i + (showNum ? 1 : 0)];
    const live = [...widths];
    const mv = (ev) => {
      live[i] = Math.max(56, startW + (ev.clientX - startX));
      if (col) col.style.width = `${live[i]}px`;
      if (tableRef.current) {
        tableRef.current.style.width = `${(showNum ? NUM_COL_W : 0) + live.reduce((a, b) => a + b, 0)}px`;
      }
    };
    const up = () => {
      document.removeEventListener('mousemove', mv);
      document.removeEventListener('mouseup', up);
      document.body.style.cursor = '';
      setWidths([...live]);
    };
    document.addEventListener('mousemove', mv);
    document.addEventListener('mouseup', up);
    document.body.style.cursor = 'col-resize';
  };

  const commitEdit = () => {
    if (!editing) return;
    const { ri, ci, value } = editing;
    const old = val(view[ri], ci);
    const oldS = old == null ? '' : String(old);
    if (String(value) === oldS) { setEditing(null); return; }
    // TYPE GUARD — reject a non-numeric string for a numeric column in place
    // (stay in the editor) instead of silently corrupting the column.
    if (colTypes && isNumericType(colTypes[ci]) && value.trim() !== '' && Number.isNaN(Number(value))) {
      if (onTypeError) onTypeError(`"${value}" is not a valid ${colTypes[ci]} for ${columns[ci]}`);
      return;
    }
    setEditing(null);
    // Optimistic: patch the local row now; revert if the caller's persist fails.
    const patch = (v) => setView((prev) => {
      const next = [...prev];
      const row = Array.isArray(next[ri]) ? [...next[ri]] : { ...next[ri] };
      if (Array.isArray(row)) row[ci] = v; else row[columns[ci]] = v;
      next[ri] = row;
      return next;
    });
    patch(value);
    Promise.resolve(onEdit(ri, ci, value, old)).catch(() => patch(old));
  };

  return (
    <div className="sv-grid-wrap" ref={wrapRef}>
      <table
        className={`sv-grid${widths ? ' fixed' : ''}`}
        ref={tableRef}
        style={widths ? { width: totalW } : undefined}
      >
        {widths && (
          <colgroup>
            {showNum && <col style={{ width: NUM_COL_W }} />}
            {columns.map((c, i) => <col key={i} style={{ width: widths[i] }} />)}
          </colgroup>
        )}
        <thead>
          <tr>
            {showNum && <th className="rownum-h">#</th>}
            {columns.map((c, i) => (
              <th
                key={i}
                className="sortable"
                onClick={(e) => { if (e.target.closest('.col-resizer')) return; handleSort(i); }}
              >
                <span className="th-label">
                  {String(c)}
                  {i === effSortCol && <span className="sort-ind">{effSortDir > 0 ? '▲' : '▼'}</span>}
                  {colTypes && colTypes[i] ? <span className="col-type">{colTypes[i]}</span> : null}
                </span>
                <span className="col-resizer" onMouseDown={(e) => startResize(e, i)} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {view.map((r, ri) => (
            <tr key={ri}>
              {showNum && <td className="rownum">{rowStart + ri}</td>}
              {columns.map((c, ci) => {
                const isEditing = editing && editing.ri === ri && editing.ci === ci;
                if (isEditing) {
                  return (
                    <td key={ci} className="editable editing">
                      <input
                        className="sdb-cell-input"
                        autoFocus
                        value={editing.value}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
                          else if (e.key === 'Escape') { e.preventDefault(); setEditing(null); }
                        }}
                        onBlur={commitEdit}
                      />
                    </td>
                  );
                }
                const cell = cellText(val(r, ci));
                return (
                  <td
                    key={ci}
                    className={editable ? 'editable' : undefined}
                    title={cell}
                    onDoubleClick={editable
                      ? () => setEditing({ ri, ci, value: val(r, ci) == null ? '' : String(val(r, ci)) })
                      : undefined}
                  >
                    {cell.length > 200 ? `${cell.slice(0, 200)}…` : cell}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
